import { useEffect, useRef, useState, type KeyboardEvent as ReactKeyboardEvent, type MouseEvent } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useRenameCatalog } from '../../api/hooks';
import { useActiveCatalog } from '../../hooks/useActiveCatalog';
import { darkTheme, FONT, GRAD } from '../../theme/tokens';
import { MenuDivider, MenuItem } from '../ui/Popover';
import { isCanvasWorkspaceRoute } from '../ui/canvasWorkspaceRoute';
import { isCatalogScopedPath, rewriteCatalogIdInPath } from './rewriteCatalogIdInPath';
import { ROW_PARAM } from './table/rowParam';
import type { CatalogResponse } from '../../types';
import { entityPlural } from './entityTerms';

const t = darkTheme;

/**
 * Sidebar card showing the currently-picked catalog. What clicking the card body
 * does depends on where you are:
 *
 * - Outside the catalog workspace (Settings, Team, Connections, the upload page,
 *   a 404) it is a link to the active catalog's table, with one catalog or many
 *   (SC-3512) — the catalog is right there in the sidebar, so it should just work.
 * - Inside the workspace it is a picker trigger when the user has 2+ catalogs
 *   (SC-1877) — click it to open a dropdown listing every catalog in the org.
 *   Selecting one updates the persisted active id and auto-navigates the user to
 *   the equivalent URL on the picked catalog (e.g. /catalogs/abc/profile →
 *   /catalogs/xyz/profile).
 *
 * The "..." button is a separate action menu (Switch catalog with 2+, Rename,
 * + New catalog) and stops propagation so it doesn't also fire the body. Its
 * "Switch catalog" is what keeps the picker reachable off the workspace.
 *
 * Renders nothing when no catalogs exist — HomeRoute redirects to /welcome
 * upstream in that case.
 */
interface ActiveCatalogCardProps {
  /** Collapsed sidebar rail (SC-2393): render just the catalog avatar. In the
   *  workspace, clicking it opens a right-anchored popover with the switch /
   *  rename / new-catalog actions (SC-2669); outside it, clicking goes to the
   *  catalog like the expanded card does, and right-click opens the popover. */
  collapsed?: boolean;
}

export function ActiveCatalogCard({ collapsed = false }: ActiveCatalogCardProps = {}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { active, catalogs, setActive } = useActiveCatalog();
  const rename = useRenameCatalog();
  const [menuOpen, setMenuOpen] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [draftName, setDraftName] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  // Pressing Enter calls commitRename → setRenaming(false) → input unmounts →
  // browser fires blur on the dying input, calling commitRename a second time
  // with stale closure values. A state guard wouldn't help (closure captures
  // the pre-commit value); a ref reads the current value.
  const committingRef = useRef(false);
  // A press on the card body while renaming ends the rename: mousedown blurs the
  // input → commitRename → `renaming` is false by the time the click lands, so
  // the click would ALSO fire the body (off the workspace: leave the page). Set
  // on every mousedown, so a press that never became a click can't swallow the
  // next real one.
  const pressEndsRenameRef = useRef(false);

  // Combined click-outside / Escape for both popovers so they don't fight each
  // other or leak listeners.
  useEffect(() => {
    if (!menuOpen && !pickerOpen) return;
    const onDocClick = (e: globalThis.MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
        setPickerOpen(false);
      }
    };
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape') {
        setMenuOpen(false);
        setPickerOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpen, pickerOpen]);

  useEffect(() => {
    if (renaming) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [renaming]);

  if (!active) return null;

  const hasMultipleCatalogs = (catalogs?.length ?? 0) > 1;
  // Same "inside the workspace" test the sidebar uses to shed its org items, so
  // the card is a link exactly where the sidebar offers the org pages — including
  // a 404 under /catalogs/:id, where this is the way back.
  const linksToCatalog = !isCanvasWorkspaceRoute(location.pathname);
  const initial = active.name.charAt(0).toUpperCase();

  const handleContextMenu = (e: MouseEvent) => {
    e.preventDefault();
    setPickerOpen(false);
    setMenuOpen(true);
  };

  const handleNewCatalog = () => {
    setMenuOpen(false);
    navigate('/welcome');
  };

  const startRename = () => {
    committingRef.current = false;
    rename.reset();
    setMenuOpen(false);
    setDraftName(active.name);
    setRenaming(true);
  };

  const commitRename = () => {
    if (committingRef.current) return;
    committingRef.current = true;
    const next = draftName.trim();
    if (next && next !== active.name) {
      rename.mutate({ id: active.id, name: next });
    }
    setRenaming(false);
  };

  const cancelRename = () => {
    committingRef.current = true;
    setRenaming(false);
  };

  const onInputKeyDown = (e: ReactKeyboardEvent<HTMLInputElement>) => {
    // Stop bubble so the card body's role-button keyhandler (Enter/Space →
    // open picker, with preventDefault) doesn't swallow the spacebar inside
    // the rename input. Mirrors the existing `onClick` stopPropagation on the
    // input — the same parent eats both event types.
    e.stopPropagation();
    if (e.key === 'Enter') {
      e.preventDefault();
      commitRename();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      cancelRename();
    }
  };

  const goToCatalog = () => {
    setMenuOpen(false);
    setPickerOpen(false);
    navigate(`/catalogs/${active.id}/entities`);
  };

  const handleBodyClick = () => {
    if (pressEndsRenameRef.current) {
      pressEndsRenameRef.current = false;
      return;
    }
    if (renaming) return;
    if (linksToCatalog) {
      goToCatalog();
      return;
    }
    if (!hasMultipleCatalogs) return;
    setMenuOpen(false);
    setPickerOpen((o) => !o);
  };

  const openSwitcher = () => {
    setMenuOpen(false);
    setPickerOpen(true);
  };

  const handlePickCatalog = (next: CatalogResponse) => {
    setPickerOpen(false);
    // Off a catalog (Connections, Team, Settings, the upload page…) there is no tab
    // to preserve, and staying put would make the pick look like it did nothing —
    // so go to the picked catalog's table. That includes the catalog that's
    // already active: picking it is how you get back to it.
    if (!isCatalogScopedPath(location.pathname)) {
      if (next.id !== active.id) setActive(next.id);
      navigate(`/catalogs/${next.id}/entities`);
      return;
    }
    if (next.id === active.id) return;
    setActive(next.id);
    // Inside a catalog: keep the user's current tab by rewriting the id segment. The
    // table's `?row=` names a row of THIS catalog (SC-3517), so it doesn't carry over:
    // in the next catalog it would open a false "no longer in the catalog".
    const search = new URLSearchParams(location.search);
    search.delete(ROW_PARAM);
    const query = search.toString();
    navigate(
      rewriteCatalogIdInPath(location.pathname, next.id) + (query ? `?${query}` : '') + location.hash,
    );
  };

  // The catalog switch options — shared by the expanded card's picker and the
  // collapsed rail's popover so both stay in step (SC-2669).
  const catalogOptions =
    hasMultipleCatalogs && catalogs
      ? catalogs.map((c) => {
          const isActive = c.id === active.id;
          return (
            <button
              key={c.id}
              type="button"
              role="option"
              aria-selected={isActive}
              onClick={() => handlePickCatalog(c)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                width: '100%',
                padding: '8px 10px',
                background: isActive ? t.sbHover : 'transparent',
                border: 'none',
                borderRadius: 6,
                color: t.text,
                fontFamily: FONT,
                fontSize: 13,
                textAlign: 'left',
                cursor: 'pointer',
              }}
              onMouseEnter={(e) => {
                if (!isActive) e.currentTarget.style.background = t.sbHover;
              }}
              onMouseLeave={(e) => {
                if (!isActive) e.currentTarget.style.background = 'transparent';
              }}
            >
              <span
                style={{
                  width: 22,
                  height: 22,
                  borderRadius: 6,
                  background: GRAD,
                  color: '#fff',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 10,
                  fontWeight: 600,
                  flexShrink: 0,
                }}
                aria-hidden="true"
              >
                {c.name.charAt(0).toUpperCase()}
              </span>
              <span
                style={{
                  flex: 1,
                  minWidth: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  fontWeight: isActive ? 600 : 400,
                }}
                title={c.name}
              >
                {c.name}
              </span>
              <span style={{ color: t.text3, fontSize: 11, flexShrink: 0 }}>
                {c.entity_count.toLocaleString()}
              </span>
            </button>
          );
        })
      : null;

  // The rename field — shared by the expanded card and the collapsed popover
  // (only one is mounted at a time, so they can share `inputRef`).
  const renameInput = (
    <input
      ref={inputRef}
      aria-label="Catalog name"
      value={draftName}
      onChange={(e) => setDraftName(e.target.value)}
      onBlur={commitRename}
      onKeyDown={onInputKeyDown}
      onClick={(e) => e.stopPropagation()}
      style={{
        width: '100%',
        padding: '4px 6px',
        background: t.sb,
        border: `1px solid ${t.accentBorder}`,
        borderRadius: 4,
        color: t.sbActive,
        fontFamily: FONT,
        fontSize: 13,
        fontWeight: 500,
        outline: 'none',
        boxShadow: `0 0 0 2px ${t.accentBg}`,
      }}
    />
  );

  // Collapsed icon rail (SC-2393 / SC-2669): the catalog avatar. Clicking it opens
  // a right-anchored popover (rather than expanding the whole rail) carrying the
  // switch list + rename + new-catalog actions, so the locked canvas icon rail
  // keeps every catalog affordance reachable. Reuses `pickerOpen` as the popover
  // flag, so the shared click-outside / Escape effect closes it.
  if (collapsed) {
    return (
      <div
        ref={containerRef}
        style={{ display: 'flex', justifyContent: 'center', padding: '4px 0 8px', position: 'relative' }}
      >
        <button
          type="button"
          onClick={() => {
            if (linksToCatalog) {
              goToCatalog();
              return;
            }
            setMenuOpen(false);
            setPickerOpen((o) => !o);
          }}
          onContextMenu={(e) => {
            e.preventDefault();
            setMenuOpen(false);
            setPickerOpen(true);
          }}
          title={active.name}
          aria-haspopup={linksToCatalog ? undefined : 'menu'}
          aria-expanded={linksToCatalog ? undefined : pickerOpen}
          aria-label={
            linksToCatalog
              ? `Go to ${active.name}`
              : `Active catalog: ${active.name}. Click to switch or manage.`
          }
          style={{
            width: 32,
            height: 32,
            borderRadius: 8,
            background: GRAD,
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 13,
            fontWeight: 600,
            border: 'none',
            cursor: 'pointer',
            flexShrink: 0,
          }}
        >
          {initial}
        </button>
        {pickerOpen && (
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: '100%',
              marginLeft: 8,
              minWidth: 220,
              background: t.surface,
              border: `1px solid ${t.border}`,
              borderRadius: 8,
              boxShadow: '0 12px 32px rgba(0,0,0,0.5)',
              padding: 4,
              zIndex: 100,
            }}
          >
            {renaming ? (
              renameInput
            ) : (
              <>
                {catalogOptions && (
                  <div role="listbox" aria-label="Switch catalog" style={{ maxHeight: 280, overflowY: 'auto' }}>
                    {catalogOptions}
                  </div>
                )}
                {catalogOptions && <MenuDivider />}
                <MenuItem onClick={startRename}>Rename</MenuItem>
                <MenuItem onClick={handleNewCatalog}>+ New catalog</MenuItem>
              </>
            )}
            {rename.isError && !renaming && (
              <div role="alert" style={{ color: t.red, fontSize: 11, padding: '4px 8px' }}>
                Rename failed. Try again.
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  const opensPicker = !linksToCatalog && hasMultipleCatalogs;
  const bodyIsButton = linksToCatalog || hasMultipleCatalogs;

  return (
    <div ref={containerRef} style={{ margin: '4px 8px 8px', position: 'relative' }}>
      <div
        onContextMenu={handleContextMenu}
        onMouseDown={(e) => {
          pressEndsRenameRef.current = renaming && e.target !== inputRef.current;
        }}
        onClick={handleBodyClick}
        role={bodyIsButton ? 'button' : undefined}
        tabIndex={bodyIsButton ? 0 : undefined}
        onKeyDown={
          bodyIsButton
            ? (e) => {
                // Only the body's own keys — Enter on the nested ⋯ button bubbles
                // here too, and must stay that button's click.
                if (e.target !== e.currentTarget) return;
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleBodyClick();
                }
              }
            : undefined
        }
        aria-label={
          linksToCatalog
            ? `Go to ${active.name}`
            : hasMultipleCatalogs
              ? `Picked catalog: ${active.name}. Click to switch.`
              : `Active catalog: ${active.name}`
        }
        aria-haspopup={opensPicker ? 'listbox' : undefined}
        aria-expanded={opensPicker ? pickerOpen : undefined}
        style={{
          position: 'relative',
          padding: '10px 12px',
          borderRadius: 10,
          background: t.sbAccentBg,
          border: `1px solid ${t.sbBorder}`,
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          cursor: bodyIsButton && !renaming ? 'pointer' : 'default',
        }}
      >
        <div
          style={{
            width: 28,
            height: 28,
            borderRadius: 8,
            background: GRAD,
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 12,
            fontWeight: 600,
            flexShrink: 0,
          }}
        >
          {initial}
        </div>
        <div style={{ minWidth: 0, flex: 1, paddingRight: 20 }}>
          {renaming ? (
            renameInput
          ) : (
            <div
              style={{
                color: t.sbActive,
                fontSize: 13,
                fontWeight: 500,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
              title={active.name}
            >
              {active.name}
            </div>
          )}
          <div style={{ color: t.sbText, fontSize: 11 }}>
            {active.entity_count.toLocaleString()} {entityPlural(active)}
            {hasMultipleCatalogs && (
              <span style={{ marginLeft: 6, opacity: 0.7 }}>
                · {catalogs!.length} catalogs
              </span>
            )}
          </div>
          {rename.isError && !renaming && (
            <div role="alert" style={{ color: t.red, fontSize: 11, marginTop: 2 }}>
              Rename failed. Try again.
            </div>
          )}
        </div>
        <button
          type="button"
          aria-label="Catalog actions"
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          onClick={(e) => {
            e.stopPropagation();
            setPickerOpen(false);
            setMenuOpen((o) => !o);
          }}
          style={{
            position: 'absolute',
            top: 4,
            right: 4,
            background: 'transparent',
            border: 'none',
            color: t.sbText,
            cursor: 'pointer',
            padding: '2px 6px',
            borderRadius: 4,
            fontSize: 16,
            lineHeight: 1,
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = t.sbHover;
            e.currentTarget.style.color = t.sbActive;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'transparent';
            e.currentTarget.style.color = t.sbText;
          }}
        >
          ⋯
        </button>
      </div>
      {menuOpen && (
        <div
          role="menu"
          style={{
            position: 'absolute',
            top: '100%',
            right: 0,
            marginTop: 4,
            minWidth: 180,
            background: t.surface,
            border: `1px solid ${t.border}`,
            borderRadius: 8,
            boxShadow: '0 12px 32px rgba(0,0,0,0.5)',
            padding: 4,
            zIndex: 100,
          }}
        >
          {hasMultipleCatalogs && <MenuItem onClick={openSwitcher}>Switch catalog</MenuItem>}
          <MenuItem onClick={startRename}>Rename</MenuItem>
          <MenuDivider />
          <MenuItem onClick={handleNewCatalog}>+ New catalog</MenuItem>
        </div>
      )}
      {pickerOpen && catalogOptions && (
        <div
          role="listbox"
          aria-label="Switch catalog"
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            marginTop: 4,
            maxHeight: 280,
            overflowY: 'auto',
            background: t.surface,
            border: `1px solid ${t.border}`,
            borderRadius: 8,
            boxShadow: '0 12px 32px rgba(0,0,0,0.5)',
            padding: 4,
            zIndex: 100,
          }}
        >
          {catalogOptions}
        </div>
      )}
    </div>
  );
}
